import { Badge, Grid, IconButton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { DownloadIcon, FilePenIcon, MultipleFilesIcon } from "src/assets/icons";
import StyledDate from "src/containers/StyledDate/StyledDate";
import { Props } from "./interfaces";
import {
  CardContainer,
  CardWrapper,
  LegislationContainer,
  NameDescriptionContainer,
  HistoryContainer,
  GenerateDocumentContainer,
  DownloadContainer,
} from "./styles";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { DocumentFileDTO } from "../../../models/documents/document-files";
import { handleDownloadFile } from "../../../services/documents";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import FlagIcon from "../../FlagIcon/FlagIcon";
import ConfigureDocumentTemplateVersionPlaceholders from "../DocumentTemplateActions/ConfigureDocumentTemplateVersionPlaceholders/ConfigureDocumentTemplateVersionPlaceholders";
import PrefillDocumentTemplateToGenerate from "../DocumentTemplateActions/PrefillDocumentTamplateToGenerate/PrefillDocumentTemplateToGenerate";
import RemoveDocumentTemplate from "../DocumentTemplateActions/RemoveDocumentTemplate";
import ViewTemplateHistory from "../DocumentTemplateActions/ViewTemplateHistory";

const DocumentTemplateCard = ({ documentTemplate, handleNotifyParent }: Props) => {
  const tRP = "components.document_templates";
  const { t } = useTranslation();

  const hasAdminAccess = useAuthorization([AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess]);

  return (
    <>
      <CardWrapper>
        <CardContainer>
          {hasAdminAccess && (
            <RemoveDocumentTemplate
              item={documentTemplate}
              handleNotifyParent={handleNotifyParent}
            />
          )}
          <LegislationContainer flex={1}>
            {documentTemplate.legislation?.code !== "ALL" && (
              <FlagIcon countryCode={documentTemplate.legislation?.code ?? ""} />
            )}
            <Typography>{`${documentTemplate.legislation?.name}`}</Typography>
          </LegislationContainer>

          <NameDescriptionContainer flex={4}>
            <Typography variant="h5">{`${documentTemplate.name}`}</Typography>
            <Typography>{`${documentTemplate.description}`}</Typography>
          </NameDescriptionContainer>

          <HistoryContainer
            flex={2}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Typography>{`${t(`${tRP}.history`)}`}</Typography>
            {documentTemplate.documentDate ? (
              <Grid>
                <Grid>
                  <Typography>{`${t(`${tRP}.last_version_date`)}`}</Typography>
                  <StyledDate
                    date={new Date(documentTemplate.documentDate).getDate()}
                    month={new Date(documentTemplate.documentDate).getMonth()}
                    year={new Date(documentTemplate.documentDate).getFullYear()}
                  />
                </Grid>
                <Badge
                  badgeContent={documentTemplate.version}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  color="primary"
                  style={{
                    height: "fit-content",
                  }}
                >
                  <ViewTemplateHistory
                    documentTemplate={documentTemplate}
                    handleNotifyParent={handleNotifyParent}
                  >
                    <MultipleFilesIcon />
                  </ViewTemplateHistory>
                </Badge>
              </Grid>
            ) : (
              <></>
            )}
          </HistoryContainer>

          <GenerateDocumentContainer
            flex={2}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Typography>{`${t(`${tRP}.generate_document_from_template`)}`}</Typography>
            {documentTemplate.template ? (
              <>
                {documentTemplate.placeHolders.length > 0 ? (
                  <>
                    <PrefillDocumentTemplateToGenerate
                      placeholders={documentTemplate.placeHolders}
                      template={documentTemplate.template}
                      handleNotifyParent={handleNotifyParent}
                      version={documentTemplate.version}
                      versionComment={documentTemplate.versionComment}
                      documentTemplateName={documentTemplate.name}
                      versionDate={documentTemplate.documentDate}
                    >
                      <FilePenIcon />
                    </PrefillDocumentTemplateToGenerate>

                    <Grid
                      style={{
                        alignSelf: "end",
                        height: "30px",
                        position: "absolute",
                        bottom: "7px",
                        right: 0,
                      }}
                    >
                      <ConfigureDocumentTemplateVersionPlaceholders
                        placeholders={documentTemplate.placeHolders}
                        handleNotifyParent={handleNotifyParent}
                        buttonType="icon"
                        template={documentTemplate.template}
                      />
                    </Grid>
                  </>
                ) : (
                  <Typography variant="body2">{`${t(`${tRP}.version_without_placeholders`)}`}</Typography>
                )}
              </>
            ) : (
              <Typography variant="body2">{`${t(`${tRP}.version_without_file`)}`}</Typography>
            )}
          </GenerateDocumentContainer>
          <DownloadContainer
            flex={2}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Typography>{`${t(`common_words.download`)}`}</Typography>
            {documentTemplate.template ? (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadFile(documentTemplate.template as DocumentFileDTO);
                }}
              >
                <DownloadIcon />
              </IconButton>
            ) : (
              <Typography variant="body2">{`${t(`${tRP}.version_without_file`)}`}</Typography>
            )}
          </DownloadContainer>
        </CardContainer>
      </CardWrapper>
    </>
  );
};

export default DocumentTemplateCard;
